import {
  campaignsApi,
  getCampaignLeadCostQueryOptions,
  StartCampaignRunError,
} from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { ConfirmBoostDialog } from "@/components/campaign/ConfirmBoostDialog";
import {
  HorizontalStepperSteps,
  StepperBottomBar,
} from "@/components/StepperSteps";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { trackDeliveryScheduled } from "@/services/amplitude.service";
import { markExpectedCampaignRun } from "@/services/push-message-fallback.service";
import { Campaign } from "@/types/campaign";
import { DeferredPromise } from "@/types/deferred-promise";
import { store } from "@/utils/jotai";
import { createStepper } from "@/utils/stepper";
import { isOneOf } from "@/utils/type-guards";
import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ComponentProps, SetStateAction, useMemo } from "react";
import { toast } from "sonner";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

const stepperSymbol = Symbol();

type BoostStepperContext = {
  campaign: Campaign;
  leadAmount: number | null;
  deliverContacts: boolean;
  deliverContactsRequirePhone: boolean;
  deliverLiveNumber: boolean;
};

const stepperContextAtom = atom<BoostStepperContext>();

const confirmDialogPropsAtom = atom<ComponentProps<
  typeof ConfirmBoostDialog
> | null>(null);

const historyStateSchema = z
  .object({
    amount: z.number(),
    skipCreditCheck: z.boolean(),
    autoSubmit: z.boolean(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute("/_dashboard/campaigns/$id/(edit)/boost")({
  staticData: {
    crumb: "Manuel levering",
    meta: {
      title: "Manuel levering",
    },
  },
  component: RouteComponent,
  beforeLoad: async ({ context, params, matches, location }) => {
    if (
      !isOneOf(context.campaign.type, "Scheduled", "OnDemand") ||
      context.trialPeriod
    )
      throw redirect({
        to: "/campaigns/$id",
        params: { id: params.id },
      });

    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    let stepperContext = store.get(stepperContextAtom);
    if (stepperContext?.campaign?.id !== params.id) {
      stepperContext = {
        campaign: context.campaign,
        leadAmount: null,
        deliverContacts: context.campaign.deliverContacts,
        deliverContactsRequirePhone:
          context.campaign.deliverContactsRequirePhone,
        deliverLiveNumber: context.campaign.deliverLiveNumber,
      };
      store.set(stepperContextAtom, stepperContext);
    }

    const useStepperContext = () => {
      const scopedAtom = useMemo(
        () =>
          atom(
            (get) => {
              const state = get(stepperContextAtom);
              if (state?.campaign.id === params.id) return state;
              return stepperContext;
            },
            (_, set, action: SetStateAction<BoostStepperContext>) => {
              set(stepperContextAtom, (prev) => {
                if (prev?.campaign.id !== params.id) return prev;
                const newState =
                  typeof action === "function" ? action(prev) : action;
                return newState;
              });
            },
          ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [params.id],
      );

      const [scopedStepperContext, setScopedStepperContext] =
        useAtom(scopedAtom);

      return [scopedStepperContext, setScopedStepperContext] as const;
    };

    const stepper = createStepper({
      symbol: stepperSymbol,
      context: stepperContext,
      steps: [
        {
          id: "lead-amount",
          name: "Antal leads",
          to: "/campaigns/$id/boost/lead-amount",
          getIsValid: ({ leadAmount }) => leadAmount !== null,
        },
        {
          id: "contact-information",
          name: "Kontaktinformation",
          to: "/campaigns/$id/boost/contact-information",
          getIsVisible: ({ campaign }) =>
            (context.appConfiguration.features.canSendContactsToIntegration ||
              context.appConfiguration.features
                .canSendLiveNumbersToIntegration) &&
            (!!campaign.completionEmail || !!campaign.exportSystem),
        },
      ],
    });

    stepper.onBeforeLoadStepRoute(Route.fullPath, matches);

    const startCampaignRun = async ({
      campaign,
      navigate,
      returnTo,
      confirm,
      options,
      skipCreditCheck,
    }: {
      campaign: Campaign;
      navigate: ReturnType<typeof Route.useNavigate>;
      returnTo: string;
      confirm: (maxCredits: number) => Promise<boolean>;
      options: Parameters<typeof campaignsApi.startCampaignRun>[1];
      skipCreditCheck?: boolean;
    }) => {
      if (!skipCreditCheck) {
        const [{ leadCost }, credits] = await Promise.all([
          context.queryClient.ensureQueryData(
            getCampaignLeadCostQueryOptions({
              ...campaign,
              ...options,
            }),
          ),
          context.queryClient.ensureQueryData(creditsQueryOptions),
        ]);

        const boostCreditEstimate = options.amount * leadCost;

        const creditsNeededNow = Math.max(
          0,
          boostCreditEstimate - credits.creditsRemaining,
        );

        const creditsNeededRestOfPeriod = Math.max(
          0,
          credits.creditEstimateRestOfPeriod +
            boostCreditEstimate -
            credits.creditsRemaining,
        );

        // Show buy credits flow if boosting will exceed credit estimate for the rest of the period.
        if (creditsNeededNow && creditsNeededRestOfPeriod) {
          const state = {
            routeStates: {
              [Route.id]: {
                amount: options.amount,
                skipCreditCheck: false,
                autoSubmit: false,
              },
            },
          };

          await navigate({
            replace: true,
            state,
          });

          await navigate({
            to: "/campaigns/$id/boost/buy-credits",
            state: {
              ...state,
              routeStates: {
                ...state.routeStates,
                [BuyCreditsRoute.id]: {
                  returnTo,
                  creditsNeededNow,
                  creditsNeededRestOfPeriod,
                },
              },
            },
          });
          return;
        }

        // If the boost has a cost, show a confirmation dialog.
        if (boostCreditEstimate) {
          const confirmed = await confirm(boostCreditEstimate);
          if (!confirmed) return;
        }
      }

      const deliveryTrackingParams = {
        source: "Manual delivery",
        campaignId: params.id,
        campaignType: campaign.type,
        options,
      } as const;

      try {
        const campaignRun = await campaignsApi.startCampaignRun(
          params.id,
          options,
        );

        trackDeliveryScheduled(context.user.id, {
          ...deliveryTrackingParams,
          requestedLeads: campaignRun.requestedLeads,
        });

        toast.success("Dine leads er på vej!");

        context.queryClient.invalidateQueries({
          queryKey: ["campaignRuns", params.id],
        });

        markExpectedCampaignRun(params.id);

        await navigate({
          to: "/campaigns/$id",
          params: { id: params.id },
        });
      } catch (error) {
        trackDeliveryScheduled(context.user.id, {
          ...deliveryTrackingParams,
          error,
        });

        if (error instanceof StartCampaignRunError) {
          switch (error.name) {
            case "runAlreadyInProgress":
              toast.error(error.message);
              return;
          }
        }

        toast.error("Noget gik galt. Prøv igen.");
        console.error(error);
      }
    };

    const useStartCampaignRunMutation = () => {
      const navigate = useNavigate();
      const { data: campaign } = context.useCampaignQuery();

      const setConfirmDialogProps = useSetAtom(confirmDialogPropsAtom);

      return useMutation({
        mutationKey: ["startCampaignRun", params.id],
        mutationFn: ({
          options,
          skipCreditCheck,
        }: {
          options: Parameters<typeof campaignsApi.startCampaignRun>[1];
          skipCreditCheck?: boolean;
        }) =>
          startCampaignRun({
            campaign,
            navigate,
            returnTo: location.pathname,
            confirm: (maxCredits: number) => {
              const { promise, resolve } = new DeferredPromise<boolean>();

              setConfirmDialogProps({
                maxCredits,
                onConfirm: () => {
                  setConfirmDialogProps(null);
                  resolve(true);
                },
                onCancel: () => {
                  setConfirmDialogProps(null);
                  resolve(false);
                },
              });

              return promise;
            },
            options,
            skipCreditCheck,
          }),
      });
    };

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
      stepper,
      useStepper: () => {
        const [stepperContext, setStepperContext] = useStepperContext();

        return stepper.useStepper({
          context: stepperContext,
          setContext: setStepperContext,
        });
      },
      useStartCampaignRunMutation,
    };
  },
  onLeave: () => {
    store.set(stepperContextAtom, undefined);
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const stepper = context.useStepper();

  const matches = useMatches();

  const hideSubStepperSteps = useMemo(
    () => stepper.visibleSteps.length <= 1,
    [stepper.visibleSteps.length],
  );

  const { showSubStepper, showBottomBar, hideSubmitButton } = useMemo(() => {
    let showSubStepper = true;
    let showBottomBar = true;
    let hideSubmitButton = false;

    const schema = z
      .object({
        showCampaignSetupSubStepper: z.boolean().catch(true),
        showCampaignSetupBottomBar: z.boolean().catch(true),
        hideSubmitButton: z.boolean().catch(false),
      })
      .optional();

    for (const match of [...matches].reverse()) {
      for (const rawData of [match.loaderData, match.staticData]) {
        if (!showSubStepper && !showBottomBar) break;

        const data = schema.parse(rawData);
        if (!data) continue;

        if (!data.showCampaignSetupSubStepper) showSubStepper = false;
        if (!data.showCampaignSetupBottomBar) showBottomBar = false;
        if (data.hideSubmitButton) hideSubmitButton = true;
      }

      if (!showSubStepper && !showBottomBar) break;
    }

    return { showSubStepper, showBottomBar, hideSubmitButton };
  }, [matches]);

  const confirmDialogProps = useAtomValue(confirmDialogPropsAtom);

  return (
    <PageHeaderProvider
      options={{
        title: "Manuel levering",
        backLinkOptions: {
          from: Route.fullPath,
          to: "/campaigns/$id",
        },
      }}
    >
      <div className="-m-8 -mt-4 flex grow flex-col xl:-m-14 xl:-mt-7">
        <div className="flex grow flex-col justify-center p-8 xl:p-14">
          {showSubStepper && stepper.visibleSteps.length ? (
            <AnimatePresence initial={false}>
              {!hideSubStepperSteps && (
                <motion.div
                  className="flex items-center justify-center"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center justify-center pb-8 xl:pb-14">
                    <HorizontalStepperSteps
                      stepper={stepper}
                      steps={stepper.visibleSteps}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : null}

          <div className="flex grow flex-col justify-center">
            <Outlet />
          </div>
        </div>

        {showBottomBar && (
          <StepperBottomBar
            stepper={stepper}
            lastStepSubmitText="Hent leads"
            hideSubmitButton={hideSubmitButton}
          />
        )}
      </div>

      {confirmDialogProps && <ConfirmBoostDialog {...confirmDialogProps} />}
    </PageHeaderProvider>
  );
}
